import { Box } from '@mui/material';
import { AxiosError } from 'axios';
import { FormikHelpers } from 'formik';
import { concat, filter, map } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { FormikTextField } from 'components';
import {
  ERROR_MESSAGE,
  IP_PLACEHOLDER,
  IP_REGEX,
} from 'constants/common.constants';
import { validationUtils } from 'utils';

type CustomerItem = {
  customerId?: string | null;
  customerIp?: string | null;
};

type Options = {
  allowToSetIP?: boolean;
  onClose: () => void;
};

const CUSTOMERS_FIELD = 'customers';

export const CUSTOMER_FIELDS = {
  ID: 'customerId' as const,
  IP: 'customerIp' as const,
};

export const useCustomersForm = ({
  allowToSetIP = false,
  onClose,
}: Options) => {
  const { t: tCommon } = useTranslation();

  const customersSchema = useMemo(() => {
    // Backend re-validates this for trusted/blocked customers (same DUPLICATE_CUSTOMERS shape
    // parsed in handleError below), so this gives instant feedback instead of a round trip there.
    // For counterparties it's not redundant: that backend silently dedupes self-duplicate
    // customerIds before validation instead of erroring, so this is the only check surfacing it.
    const checkDuplicates = function (
      this: Yup.TestContext,
      customers: CustomerItem[] | undefined,
    ) {
      if (!Array.isArray(customers)) {
        return true;
      }

      const errors: Yup.ValidationError[] = [];
      const seenIds = new Map<string, number[]>();
      const seenIps = new Map<string, number[]>();

      customers.forEach((c, i) => {
        if (c?.[CUSTOMER_FIELDS.ID]) {
          const idxs = seenIds.get(c[CUSTOMER_FIELDS.ID]!) ?? [];
          idxs.push(i);
          seenIds.set(c[CUSTOMER_FIELDS.ID]!, idxs);
        }
        if (c?.[CUSTOMER_FIELDS.IP]) {
          const idxs = seenIps.get(c[CUSTOMER_FIELDS.IP]!) ?? [];
          idxs.push(i);
          seenIps.set(c[CUSTOMER_FIELDS.IP]!, idxs);
        }
      });

      seenIds.forEach((indices) => {
        if (indices.length > 1) {
          indices.forEach((idx) => {
            errors.push(
              this.createError({
                path: `${this.path}[${idx}].${CUSTOMER_FIELDS.ID}`,
                message: tCommon('errors.duplicate_customer_id'),
              }),
            );
          });
        }
      });

      seenIps.forEach((indices) => {
        if (indices.length > 1) {
          indices.forEach((idx) => {
            errors.push(
              this.createError({
                path: `${this.path}[${idx}].${CUSTOMER_FIELDS.IP}`,
                message: tCommon('errors.duplicate_customer_ip'),
              }),
            );
          });
        }
      });

      if (errors.length > 0) {
        throw new Yup.ValidationError(errors);
      }

      return true;
    };

    if (allowToSetIP) {
      return Yup.array()
        .of(
          Yup.object()
            .shape({
              [CUSTOMER_FIELDS.ID]: Yup.string().nullable().optional(),
              [CUSTOMER_FIELDS.IP]: Yup.string()
                .nullable()
                .optional()
                .matches(IP_REGEX, tCommon('errors.customer_ip_invalid')),
            })
            .test(
              'at-least-one',
              tCommon('errors.required'),
              (value) =>
                !!(value?.[CUSTOMER_FIELDS.ID] || value?.[CUSTOMER_FIELDS.IP]),
            ),
        )
        .min(1, tCommon('errors.required'))
        .test('unique-customers', '', checkDuplicates);
    }

    return Yup.array()
      .of(
        Yup.object().shape({
          [CUSTOMER_FIELDS.ID]: Yup.string().required(
            tCommon('errors.required'),
          ),
        }),
      )
      .test('unique-customers', '', checkDuplicates);
  }, [allowToSetIP, tCommon]);

  const renderCustomerItem = useCallback(
    ({ index }: { index: number }) => (
      <Box className="tw-flex tw-flex-col sm:tw-flex-row tw-w-full tw-gap-2">
        <FormikTextField
          name={`${CUSTOMERS_FIELD}[${index}].${CUSTOMER_FIELDS.ID}`}
          label={tCommon('common.customer_id')}
          required={!allowToSetIP}
          fullWidth
        />
        {allowToSetIP && (
          <FormikTextField
            name={`${CUSTOMERS_FIELD}[${index}].${CUSTOMER_FIELDS.IP}`}
            placeholder={IP_PLACEHOLDER}
            label={tCommon('common.ip')}
            fullWidth
          />
        )}
      </Box>
    ),
    [tCommon, allowToSetIP],
  );

  const handleImportIdConfirm = useCallback(
    (
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
      ) => void,
      setFieldTouched: (
        field: string,
        isTouched?: boolean,
        shouldValidate?: boolean,
      ) => void,
      currentCustomers: CustomerItem[],
      ids: string[],
    ) => {
      const existingSet = new Set(map(currentCustomers, CUSTOMER_FIELDS.ID));
      const newCustomers = filter(ids, (id) => !existingSet.has(id)).map((v) =>
        allowToSetIP
          ? { [CUSTOMER_FIELDS.ID]: v, [CUSTOMER_FIELDS.IP]: '' }
          : { [CUSTOMER_FIELDS.ID]: v },
      );
      setFieldValue(
        CUSTOMERS_FIELD,
        concat(currentCustomers, newCustomers),
        false,
      );
      newCustomers.forEach((_, i) => {
        const idx = currentCustomers.length + i;
        const isLast = i === newCustomers.length - 1;
        setFieldTouched(
          `${CUSTOMERS_FIELD}[${idx}].${CUSTOMER_FIELDS.ID}`,
          true,
          isLast && !allowToSetIP,
        );
        if (allowToSetIP) {
          setFieldTouched(
            `${CUSTOMERS_FIELD}[${idx}].${CUSTOMER_FIELDS.IP}`,
            true,
            isLast,
          );
        }
      });
      onClose();
    },
    [allowToSetIP, onClose],
  );

  const handleImportIpConfirm = useCallback(
    (
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
      ) => void,
      setFieldTouched: (
        field: string,
        isTouched?: boolean,
        shouldValidate?: boolean,
      ) => void,
      currentCustomers: CustomerItem[],
      ips: string[],
    ) => {
      const existingSet = new Set(
        map(currentCustomers, CUSTOMER_FIELDS.IP).filter(Boolean),
      );
      const newCustomers = filter(ips, (ip) => !existingSet.has(ip)).map(
        (v) => ({ [CUSTOMER_FIELDS.ID]: '', [CUSTOMER_FIELDS.IP]: v }),
      );
      setFieldValue(
        CUSTOMERS_FIELD,
        concat(currentCustomers, newCustomers),
        false,
      );
      newCustomers.forEach((_, i) => {
        const idx = currentCustomers.length + i;
        const isLast = i === newCustomers.length - 1;
        setFieldTouched(
          `${CUSTOMERS_FIELD}[${idx}].${CUSTOMER_FIELDS.ID}`,
          true,
          false,
        );
        setFieldTouched(
          `${CUSTOMERS_FIELD}[${idx}].${CUSTOMER_FIELDS.IP}`,
          true,
          isLast,
        );
      });
      onClose();
    },
    [onClose],
  );

  const handleError = useCallback(
    (
      error: AxiosError,
      formikHelpers: FormikHelpers<any>,
      formValues: { customers: CustomerItem[] },
    ) => {
      const data = error.response?.data as any;
      if (data?.message === ERROR_MESSAGE.DUPLICATE_CUSTOMERS) {
        const duplicateIds = new Set<string>(data.customerIds ?? []);
        const duplicateIps = new Set<string>(data.customerIps ?? []);
        formikHelpers.setErrors({
          customers: map(formValues.customers, (customer) => {
            const idError =
              customer[CUSTOMER_FIELDS.ID] &&
              duplicateIds.has(customer[CUSTOMER_FIELDS.ID]!)
                ? {
                    [CUSTOMER_FIELDS.ID]: tCommon(
                      'errors.customer_id_already_assigned',
                    ),
                  }
                : undefined;
            const ipError =
              customer[CUSTOMER_FIELDS.IP] &&
              duplicateIps.has(customer[CUSTOMER_FIELDS.IP]!)
                ? {
                    [CUSTOMER_FIELDS.IP]: tCommon(
                      'errors.customer_ip_already_assigned',
                    ),
                  }
                : undefined;
            return idError || ipError ? { ...idError, ...ipError } : undefined;
          }) as any,
        });
      } else {
        formikHelpers.setErrors(validationUtils.getFormErrors(error));
      }
    },
    [tCommon],
  );

  return {
    customersSchema,
    renderCustomerItem,
    handleImportIdConfirm,
    handleImportIpConfirm,
    handleError,
  };
};
